import { version } from '../constants/env';
import {
  ERRORS,
  LS_HAS_USER_UNSUBSCRIBED_KEY,
  LS_TIME_KEY,
  PERMISSION_GRANTED,
  SCP_COOKIE_PARAM,
} from '../constants/settings';

function getTimemarkName() {
  return `${LS_TIME_KEY}:${version}`;
}

function clearSimilarTimemarks() {
  // подчищаем старые версии
  const markName = getTimemarkName();
  const data = Object.keys(window.localStorage)
    .filter((name) => name.indexOf(LS_TIME_KEY) === 0 && name !== markName)
    .reduce((acc, name) => {
      window.localStorage.removeItem(name);
      return {
        ...acc,
        [name]: window.localStorage.getItem(name),
      };
    }, {});

  return Promise.resolve(data);
}

/**
 * Получаем служебную куку s_cp
 * @returns {array} fields - массив полей в куке (да-да, их может быть больше одной ¯\_(ツ)_/¯)
 */
function getScpCookie() {
  const scpCookieMatch = /s_cp=([^;]*)/.exec(window.document.cookie);
  const scpString = scpCookieMatch ? scpCookieMatch[1] : '';
  const fields = scpString ? scpString.split('|') : [];
  return fields;
}

/**
 * Как ты уже догадался, записываем в служебную куку s_cp
 * @param {array} fields – массив полей куки
 */
function setScpCookie(fields: string[]) {
  if (fields && fields.length) {
    const domain =
      window.location.hostname.indexOf('devmail') === -1
        ? '.mail.ru'
        : '.devmail.ru';
    window.document.cookie = `s_cp=${fields.join(
      '|'
    )};path=/;domain=${domain};max-age=${60 * 60 * 24 * 365}`;
  }
}

/**
 * Получаем время
 * @param {boolean} useLocalStorage – используем куку или localstorage
 * @returns {number} time - таймстемп
 */
function getTime(useLocalStorage: boolean): number {
  const markName = getTimemarkName();
  let time: string | null;

  if (useLocalStorage) {
    time = window.localStorage.getItem(markName);
  } else {
    let fields = getScpCookie();
    fields = fields.filter((field) => {
      return field.indexOf(SCP_COOKIE_PARAM) > -1;
    });

    time = String(fields.length && fields[0].split('=')[1]);
  }
  return parseInt(String(time || 0), 10);
}

/**
 * Записываем время
 * @param {string} timestamp
 * @param {boolean} useLocalStorage – используем куку или localstorage
 */
function setTime(timestamp: number, useLocalStorage: boolean) {
  if (useLocalStorage) {
    const markName = getTimemarkName();
    window.localStorage.setItem(markName, String(timestamp));
  } else {
    let fields = getScpCookie();

    fields = fields.filter((field) => {
      return field.indexOf(SCP_COOKIE_PARAM) === -1;
    });

    fields.push(`${SCP_COOKIE_PARAM}=${timestamp}`);
    setScpCookie(fields);
  }
}

export function clearTimemark(useLocalStorage: boolean) {
  const markName = getTimemarkName();
  const value = getTime(useLocalStorage);

  if (useLocalStorage) {
    window.localStorage.removeItem(markName);
  }

  return Promise.resolve(value);
}

export function checkTimeMark(
  delta: number,
  useLocalStorage: boolean,
  ignoreTime?: boolean
): Promise<number> {
  const date = new Date();

  return clearSimilarTimemarks().then(() => {
    const prevTimestamp = getTime(useLocalStorage);
    const currTimestamp = date.getTime();
    const nextTimestamp = currTimestamp + delta;

    return new Promise((resolve, reject) => {
      if (ignoreTime) {
        resolve(0);
      } else if (
        typeof prevTimestamp === 'undefined' ||
        prevTimestamp === null
      ) {
        resolve(nextTimestamp);
      } else if (currTimestamp > prevTimestamp) {
        resolve(nextTimestamp);
      } else {
        reject(ERRORS.TIMEMARK_HAS_BEEN_SET);
      }
    });
  });
}

export function updateTimeMark(delta: number, useLocalStorage: boolean) {
  if (typeof delta === 'undefined' || delta === null) {
    return Promise.reject(new Error(ERRORS.TIMEMARK_DELTA_INVALID.message));
  }

  return clearTimemark(useLocalStorage)
    .then(() => checkTimeMark(delta, useLocalStorage))
    .then((markValue) => {
      void setTime(markValue, useLocalStorage);

      return markValue;
    });
}

/**
 * @returns {boolean} true - пользователь вручную отписался
 *                    false - пользователь не запрещал пуши
 */
export function checkUnsubscribeFlag(): boolean {
  return Boolean(window.localStorage.getItem(LS_HAS_USER_UNSUBSCRIBED_KEY));
}

/**
 * @param {boolean} isUnsubscribed
 */
export function setUnsubscribeFlag(isUnsubscribed: boolean) {
  if (isUnsubscribed) {
    window.localStorage.setItem(LS_HAS_USER_UNSUBSCRIBED_KEY, '1');
  } else {
    window.localStorage.removeItem(LS_HAS_USER_UNSUBSCRIBED_KEY);
  }
}

/**
 * Функция подписывается на изменение состояния прав на подписку уведамлений.
 * На данный момент следит за состоянием `denied`, когда ползователь выбрал блокировку уведомлений и обновляет время,
 * до которого не показывать предложение подписаться.
 * @param {number} delta – время, сколько не показывать предложение подписаться.
 * @param {boolean} useLocalStorage – флаг, используем ли localstorage. Если true, то ничего не делаем.
 */
export function observePermission(delta: number, useLocalStorage: boolean) {
  if (useLocalStorage) {
    return;
  }
  const { permissions } = window.navigator;
  if (permissions) {
    void permissions.query({ name: 'notifications' }).then((result) => {
      const notifyPermissions = result;
      notifyPermissions.onchange = () => {
        if (notifyPermissions.state === 'denied') {
          void updateTimeMark(delta, useLocalStorage);
        }
      };
    });
  }
}

export function getPermission() {
  return {
    browserPermission: Notification.permission === PERMISSION_GRANTED,
    userPermission: !checkUnsubscribeFlag(),
  };
}

export function getViewportSizes(): {
  top: number;
  left: number;
  width: number;
  height: number;
} {
  return {
    top:
      window.pageYOffset ||
      document.body.scrollTop ||
      document.documentElement.scrollTop,
    left:
      window.pageXOffset ||
      document.body.scrollLeft ||
      document.documentElement.scrollLeft,
    width:
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth,
    height:
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight,
  };
}

/**
 * @see https://stackoverflow.com/a/16427747/15266997
 * @returns доступен ли `storage`
 */
const isStorageAvailable = (
  storage: 'localStorage' | 'sessionStorage'
): boolean => {
  try {
    window[storage].setItem('___test_media', '___test_media');
    window[storage].removeItem('___test_media');
    return true;
  } catch {
    return false;
  }
};

/**
 * @see https://stackoverflow.com/a/16427747/15266997
 * @returns доступен ли `window.localStorage`
 */
export const isLocalStorageAvailable = () => isStorageAvailable('localStorage');
